import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * 쳐서 찾는 고르기 칸(#439 배상준 「계정 선택란에 타이핑으로 찾을 수 있으면」).
 * `<select>` 는 한글 입력으로 못 찾는다 — 칸에 치면 그 글자가 든 항목만 아래에 뜬다.
 *
 * - 고를 수 있는 값은 `options` 뿐이다(자유입력 아님). 친 글자가 항목과 똑같으면 그걸로, 아니면 칸을 떠날 때 원래 값으로 돌아간다.
 * - ↑↓ 로 옮기고 Enter 로 고른다. Esc = 닫고 원래 값.
 * - 목록은 `document.body` 에 띄운다(fixed) — 창(Modal)·표 스크롤 상자 안에 두면 잘린다(`ColumnFilter` 와 같은 이유).
 */
export default function SearchSelect({
  value, options, onChange, placeholder = '선택', disabled, className = 'field', extraLabel,
}: {
  value: string;
  options: string[];
  onChange: (v: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** 목록 밖 현재 값에 붙일 표시(예: 「⚠ 미등록」) — 값은 그대로 둔다. */
  extraLabel?: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState<string | null>(null); // null = 치는 중 아님(값을 보여 준다)
  const [hi, setHi] = useState(0);

  const shown = useMemo(() => {
    const t = (term ?? '').trim().toLowerCase();
    return t ? options.filter((o) => o.toLowerCase().includes(t)) : options;
  }, [term, options]);
  useEffect(() => { setHi(0); }, [term]);

  function pick(v: string) {
    onChange(v);
    setTerm(null);
    setOpen(false);
  }
  function leave() {
    // 친 글자가 항목과 똑같으면 그걸로 — 아니면 원래 값(목록 밖 글자를 저장하지 않는다).
    const t = (term ?? '').trim();
    if (term !== null && t && options.includes(t) && t !== value) onChange(t);
    if (term !== null && !t) onChange('');
    setTerm(null);
    setOpen(false);
  }

  const label = value && extraLabel && !options.includes(value) ? `${value} ${extraLabel}` : value;
  return (
    <>
      <input
        ref={input}
        className={className}
        value={term ?? label}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        onFocus={(e) => { setOpen(true); e.currentTarget.select(); }}
        onClick={() => setOpen(true)}
        onChange={(e) => { setTerm(e.target.value); setOpen(true); }}
        onBlur={leave}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHi((h) => Math.min(h + 1, shown.length - 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
          else if (e.key === 'Enter') {
            if (open && shown[hi] !== undefined) { e.preventDefault(); pick(shown[hi]); }
          } else if (e.key === 'Escape') {
            if (open) { e.preventDefault(); e.stopPropagation(); setTerm(null); setOpen(false); }
          }
        }}
      />
      {open && !disabled && input.current && (
        <List anchor={input.current} items={shown} hi={hi} value={value} onHover={setHi} onPick={pick} />
      )}
    </>
  );
}

function List({ anchor, items, hi, value, onHover, onPick }: {
  anchor: HTMLElement; items: string[]; hi: number; value: string;
  onHover: (i: number) => void; onPick: (v: string) => void;
}) {
  const box = useRef<HTMLUListElement>(null);
  const [pos, setPos] = useState({ left: 0, top: 0, width: 0 });
  useLayoutEffect(() => {
    const place = () => {
      const r = anchor.getBoundingClientRect();
      setPos({ left: r.left, top: r.bottom + 2, width: Math.max(r.width, 200) });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => { window.removeEventListener('scroll', place, true); window.removeEventListener('resize', place); };
  }, [anchor]);
  useEffect(() => {
    box.current?.querySelector<HTMLElement>(`[data-i="${hi}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [hi]);

  return createPortal(
    <ul ref={box} style={{ left: pos.left, top: pos.top, width: pos.width }}
        className="fixed z-[70] max-h-64 overflow-y-auto rounded-lg border border-line bg-surface py-1 text-sm shadow-lg">
      {items.map((o, i) => (
        <li key={o} data-i={i}
            // mousedown 에서 고른다 — click 이면 그 전에 칸의 blur 가 목록을 닫는다.
            onMouseDown={(e) => { e.preventDefault(); onPick(o); }}
            onMouseEnter={() => onHover(i)}
            className={`cursor-pointer px-2 py-1 ${i === hi ? 'bg-brand-50 text-brand-700' : ''} ${o === value ? 'font-semibold' : ''}`}>
          {o}
        </li>
      ))}
      {items.length === 0 && <li className="px-2 py-1 text-ink-faint">찾는 계정이 없습니다</li>}
    </ul>,
    document.body,
  );
}
